import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  LinearProgress,
  Paper,
  useTheme,
  alpha,
  Fade,
  Tooltip as MuiTooltip,
  Avatar,
  AvatarGroup,
  Tabs,
  Tab,
  Divider,
  Button,
  Badge,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Category as CategoryIcon,
  LocalOffer as LocalOfferIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Campaign as CampaignIcon,
  YouTube as YouTubeIcon,
  AutoGraph as AutoGraphIcon,
  Analytics as AnalyticsIcon,
  Leaderboard as LeaderboardIcon,
  Bolt as BoltIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Schedule as ScheduleIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell } from 'recharts';
import { Product } from '../types/marketing';
import { Lead } from '../types/lead';
import * as mockDataService from '../services/mockData';
import { setLeads } from '../redux/slices/leadSlice';
import { setProducts } from '../redux/slices/productSlice';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const leads = useSelector((state: RootState) => state.leads.items);
  const products = useSelector((state: RootState) => state.products.items);
  const [animate, setAnimate] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [leadsData, productsData] = await Promise.all([
          mockDataService.getLeads(),
          mockDataService.getProducts(),
        ]);
        console.log('Dados carregados:', { leads: leadsData, products: productsData });
        dispatch(setLeads(leadsData));
        dispatch(setProducts(productsData));
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };

    loadData();
    setAnimate(true);
  }, [dispatch]);

  // Cálculos para os cards de leads
  const leadsAtivos = leads.filter(lead => lead.status === 'active' || lead.status === 'novo').length;
  const leadsNovos = leads.filter(lead => {
    if (!lead.createdAt) return false;
    const leadDate = new Date(lead.createdAt);
    const hoje = new Date();
    return leadDate.getMonth() === hoje.getMonth() && leadDate.getFullYear() === hoje.getFullYear();
  }).length;
  const leadsPrioritarios = leads.filter(lead => lead.status === 'prioritario').length;

  // Cálculos para os cards de produtos
  const produtosPorCategoria = products.reduce((acc: { [key: string]: number }, product: Product) => {
    const categoria = product.category || 'Outros';
    acc[categoria] = (acc[categoria] || 0) + 1;
    return acc;
  }, {});

  const categoriasMaisPopulares = Object.entries(produtosPorCategoria)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const valorTotalProdutos = products.reduce((total, product) => total + (product.price || 0), 0);

  // Dados simulados para os gráficos com mais pontos de dados
  const performanceData = [
    { month: 'Jan', vendas: 4, leads: 8, receita: 400000 },
    { month: 'Fev', vendas: 3, leads: 6, receita: 300000 },
    { month: 'Mar', vendas: 5, leads: 9, receita: 500000 },
    { month: 'Abr', vendas: 7, leads: 12, receita: 700000 },
    { month: 'Mai', vendas: 6, leads: 10, receita: 600000 },
    { month: 'Jun', vendas: 8, leads: 15, receita: 800000 },
    { month: 'Jul', vendas: 10, leads: 18, receita: 1000000 },
  ];

  const recentActivities = [
    { id: 1, type: 'lead', message: 'Novo lead interessado em apartamento', time: '5min', status: 'new' },
    { id: 2, type: 'produto', message: 'Imóvel vendido por R$ 450.000', time: '30min', status: 'success' },
    { id: 3, type: 'reuniao', message: 'Reunião agendada com cliente', time: '1h', status: 'pending' },
    { id: 4, type: 'lead', message: 'Lead convertido em cliente', time: '2h', status: 'success' },
    { id: 5, type: 'produto', message: 'Nova avaliação de imóvel', time: '3h', status: 'pending' },
  ];

  const upcomingEvents = [
    { id: 1, title: 'Visita ao imóvel', time: '14:00', client: 'João Silva', type: 'visita' },
    { id: 2, title: 'Assinatura de contrato', time: '16:30', client: 'Maria Santos', type: 'contrato' },
    { id: 3, title: 'Avaliação de imóvel', time: '10:00', client: 'Pedro Oliveira', type: 'avaliacao' },
    { id: 4, title: 'Reunião com investidor', time: '11:30', client: 'Carlos Mendes', type: 'reuniao' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return theme.palette.info.main;
      case 'success': return theme.palette.success.main;
      case 'pending': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'visita': return theme.palette.primary.main;
      case 'contrato': return theme.palette.success.main;
      case 'avaliacao': return theme.palette.warning.main;
      case 'reuniao': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  // Dados simulados de redes sociais
  const socialMediaData = {
    instagram: {
      followers: 5248,
      engagement: 4.8,
      posts: 127,
      growth: 12.5
    },
    facebook: {
      followers: 3820,
      engagement: 3.2,
      posts: 98,
      growth: 8.3
    },
    youtube: {
      followers: 1250,
      engagement: 6.7,
      posts: 45,
      growth: 15.2
    },
    tiktok: {
      followers: 2890,
      engagement: 8.9,
      posts: 78,
      growth: 24.7
    },
    x: {
      followers: 1680,
      engagement: 2.5,
      posts: 210,
      growth: 5.8
    },
    google: {
      followers: 840,
      engagement: 4.1,
      posts: 62,
      growth: 9.3
    }
  };

  // Dados simulados de WhatsApp
  const whatsappData = {
    activeChats: 28,
    newMessages: 12,
    responseRate: 92,
    averageResponseTime: 8, // minutos
    automatedResponses: 45,
    leadConversions: 8
  };

  // Dados simulados de e-mail
  const emailData = {
    totalEmails: 124,
    unread: 18,
    sent: 87,
    openRate: 68,
    responseRate: 42,
    automatedEmails: 35
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Cards principais com efeito de gradiente e animação */}
        <Fade in={animate} timeout={1000}>
          <Grid container item spacing={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
              }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" sx={{ mb: 0.5 }}>{leadsAtivos}</Typography>
                      <Typography variant="subtitle2">Leads Ativos</Typography>
                      <Stack direction="row" spacing={1} mt={1}>
                        <Chip
                          label={`${leadsNovos} novos`}
                          size="small"
                          sx={{
                            bgcolor: alpha('#fff', 0.2),
                            color: 'white',
                            fontSize: '0.75rem',
                          }}
                        />
                        <Chip
                          label={`${leadsPrioritarios} prioritários`}
                          size="small"
                          sx={{
                            bgcolor: alpha('#fff', 0.2),
                            color: 'white',
                            fontSize: '0.75rem',
                          }}
                        />
                      </Stack>
                    </Box>
                    <PeopleIcon fontSize="large" sx={{ opacity: 0.8 }} />
                  </Stack>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} /> +{leadsNovos} este mês
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={70}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'white',
                          borderRadius: 3,
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                color: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.4)}`,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
              }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" sx={{ mb: 0.5 }}>{products.length}</Typography>
                      <Typography variant="subtitle2">Produtos Cadastrados</Typography>
                      <Stack direction="row" spacing={1} mt={1}>
                        {categoriasMaisPopulares.map(([categoria, quantidade], index) => (
                          <Chip
                            key={categoria}
                            label={`${quantidade} ${categoria}`}
                            size="small"
                            sx={{
                              bgcolor: alpha('#fff', 0.2),
                              color: 'white',
                              fontSize: '0.75rem',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                    <CategoryIcon fontSize="large" sx={{ opacity: 0.8 }} />
                  </Stack>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      Valor total: R$ {(valorTotalProdutos / 1000).toFixed(1)}k
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={85}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'white',
                          borderRadius: 3,
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                color: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.4)}`,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
              }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" sx={{ mb: 0.5 }}>R$ 1,2M</Typography>
                      <Typography variant="subtitle2">Vendas do Mês</Typography>
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ArrowUpwardIcon sx={{ fontSize: 14 }} /> +15% vs mês anterior
                      </Typography>
                    </Box>
                    <MoneyIcon fontSize="large" sx={{ opacity: 0.8 }} />
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={60}
                    sx={{
                      mt: 2,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: 'white',
                        borderRadius: 3,
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                color: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.4)}`,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
              }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" sx={{ mb: 0.5 }}>89%</Typography>
                      <Typography variant="subtitle2">Taxa de Conversão</Typography>
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ArrowUpwardIcon sx={{ fontSize: 14 }} /> +7% este mês
                      </Typography>
                    </Box>
                    <SpeedIcon fontSize="large" sx={{ opacity: 0.8 }} />
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={89}
                    sx={{
                      mt: 2,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: 'white',
                        borderRadius: 3,
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Fade>

        {/* Resumo de Automações e IA */}
        <Grid item xs={12}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoGraphIcon color="primary" /> Resumo de Automações e Inteligência Artificial
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <BoltIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" align="center" fontWeight="bold">
                      {whatsappData.automatedResponses + emailData.automatedEmails}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Respostas Automatizadas
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <LeaderboardIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" align="center" fontWeight="bold">
                      {whatsappData.leadConversions}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Conversões via IA
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <AnalyticsIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" align="center" fontWeight="bold">
                      {Math.round((whatsappData.responseRate + emailData.responseRate) / 2)}%
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Taxa Média de Resposta
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <ChatBubbleOutlineIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" align="center" fontWeight="bold">
                      {whatsappData.activeChats}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Conversas Ativas
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Gráfico de Performance com área preenchida */}
        <Grid item xs={12} md={8}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="primary" /> Desempenho
              </Typography>
              <Box sx={{ height: 350, p: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={performanceData}>
                    <defs>
                      <linearGradient id="colorVendas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                    <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="vendas"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#colorVendas)"
                      name="Vendas"
                    />
                    <Area
                      type="monotone"
                      dataKey="leads"
                      stroke={theme.palette.secondary.main}
                      fillOpacity={1}
                      fill="url(#colorLeads)"
                      name="Leads"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Calendário de Eventos */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" /> Próximos Eventos
              </Typography>
              <Stack spacing={2}>
                {upcomingEvents.map(event => (
                  <Paper
                    key={event.id}
                    sx={{
                      p: 2,
                      bgcolor: alpha(getEventTypeColor(event.type), 0.05),
                      borderLeft: `4px solid ${getEventTypeColor(event.type)}`,
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateX(5px)',
                        bgcolor: alpha(getEventTypeColor(event.type), 0.1),
                      },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">{event.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon fontSize="small" /> {event.time} - {event.client}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Feed de Atividades */}
        <Grid item xs={12}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" /> Atividades Recentes
              </Typography>
              <Stack spacing={2}>
                {recentActivities.map(activity => (
                  <Paper
                    key={activity.id}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      bgcolor: alpha(getStatusColor(activity.status), 0.05),
                      borderRadius: 2,
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateX(5px)',
                        bgcolor: alpha(getStatusColor(activity.status), 0.1),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: getStatusColor(activity.status),
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">{activity.message}</Typography>
                      <Typography variant="caption" color="text.secondary">{activity.time} atrás</Typography>
                    </Box>
                    <Chip
                      label={activity.type}
                      size="small"
                      sx={{
                        bgcolor: alpha(getStatusColor(activity.status), 0.1),
                        color: getStatusColor(activity.status),
                      }}
                    />
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* WhatsApp */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon color="primary" /> WhatsApp IA Bot
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Conversas ativas:</Typography>
                  <Chip 
                    label={whatsappData.activeChats} 
                    color="primary" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Novas mensagens:</Typography>
                  <Chip 
                    label={whatsappData.newMessages} 
                    color="error" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Taxa de resposta:</Typography>
                  <Chip 
                    label={`${whatsappData.responseRate}%`} 
                    color="success" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Tempo médio de resposta:</Typography>
                  <Chip 
                    label={`${whatsappData.averageResponseTime} min`} 
                    color="info" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Respostas automatizadas:</Typography>
                  <Chip 
                    label={whatsappData.automatedResponses} 
                    color="secondary" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Conversões de leads:</Typography>
                  <Chip 
                    label={whatsappData.leadConversions} 
                    color="warning" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Divider />
                <Button variant="contained" startIcon={<WhatsAppIcon />} fullWidth>
                  Abrir WhatsApp IA Bot
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* E-mail */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="primary" /> E-mail com IA
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Total de e-mails:</Typography>
                  <Chip 
                    label={emailData.totalEmails} 
                    color="primary" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Não lidos:</Typography>
                  <Chip 
                    label={emailData.unread} 
                    color="error" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Enviados:</Typography>
                  <Chip 
                    label={emailData.sent} 
                    color="info" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Taxa de abertura:</Typography>
                  <Chip 
                    label={`${emailData.openRate}%`} 
                    color="success" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Taxa de resposta:</Typography>
                  <Chip 
                    label={`${emailData.responseRate}%`} 
                    color="secondary" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">E-mails automatizados:</Typography>
                  <Chip 
                    label={emailData.automatedEmails} 
                    color="warning" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <Divider />
                <Button variant="contained" startIcon={<EmailIcon />} fullWidth>
                  Abrir E-mail Response
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Redes Sociais */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.3s ease-in-out',
            '&:hover': { transform: 'translateY(-5px)' },
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InstagramIcon color="primary" /> Redes Sociais
              </Typography>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="basic tabs example">
                <Tab label="Instagram" />
                <Tab label="Facebook" />
                <Tab label="YouTube" />
                <Tab label="TikTok" />
                <Tab label="X" />
                <Tab label="Google" />
              </Tabs>
              {activeTab === 0 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body1">Seguidores: {socialMediaData.instagram.followers}</Typography>
                  <Typography variant="body1">Engajamento: {socialMediaData.instagram.engagement}%</Typography>
                  <Typography variant="body1">Posts: {socialMediaData.instagram.posts}</Typography>
                  <Typography variant="body1">Crescimento: {socialMediaData.instagram.growth}%</Typography>
                </Stack>
              )}
              {activeTab === 1 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body1">Seguidores: {socialMediaData.facebook.followers}</Typography>
                  <Typography variant="body1">Engajamento: {socialMediaData.facebook.engagement}%</Typography>
                  <Typography variant="body1">Posts: {socialMediaData.facebook.posts}</Typography>
                  <Typography variant="body1">Crescimento: {socialMediaData.facebook.growth}%</Typography>
                </Stack>
              )}
              {activeTab === 2 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body1">Seguidores: {socialMediaData.youtube.followers}</Typography>
                  <Typography variant="body1">Engajamento: {socialMediaData.youtube.engagement}%</Typography>
                  <Typography variant="body1">Posts: {socialMediaData.youtube.posts}</Typography>
                  <Typography variant="body1">Crescimento: {socialMediaData.youtube.growth}%</Typography>
                </Stack>
              )}
              {activeTab === 3 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body1">Seguidores: {socialMediaData.tiktok.followers}</Typography>
                  <Typography variant="body1">Engajamento: {socialMediaData.tiktok.engagement}%</Typography>
                  <Typography variant="body1">Posts: {socialMediaData.tiktok.posts}</Typography>
                  <Typography variant="body1">Crescimento: {socialMediaData.tiktok.growth}%</Typography>
                </Stack>
              )}
              {activeTab === 4 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body1">Seguidores: {socialMediaData.x.followers}</Typography>
                  <Typography variant="body1">Engajamento: {socialMediaData.x.engagement}%</Typography>
                  <Typography variant="body1">Posts: {socialMediaData.x.posts}</Typography>
                  <Typography variant="body1">Crescimento: {socialMediaData.x.growth}%</Typography>
                </Stack>
              )}
              {activeTab === 5 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body1">Seguidores: {socialMediaData.google.followers}</Typography>
                  <Typography variant="body1">Engajamento: {socialMediaData.google.engagement}%</Typography>
                  <Typography variant="body1">Posts: {socialMediaData.google.posts}</Typography>
                  <Typography variant="body1">Crescimento: {socialMediaData.google.growth}%</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;